import { useMemo } from "react";
import { Editor } from "@tinymce/tinymce-react";

import { ITemplateEditorMergeTag } from "../../../models/TemplateEditor";

interface CustomTinyMceEditorProps {
  content: string;
  setContent: (value: string) => void;
  mergeTags: ITemplateEditorMergeTag[];
  mode: "html" | "text";
  height?: number;
  placeholder?: string;
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const toEditorValue = (content: string, mode: "html" | "text") => {
  if (mode === "html") return content ?? "";
  return escapeHtml(content ?? "").replace(/\r\n/g, "\n").replace(/\n/g, "<br />");
};

export default function CustomTinyMceEditor({
  content,
  setContent,
  mergeTags,
  mode,
  height = 380,
  placeholder = "Start typing..."
}: CustomTinyMceEditorProps) {
  const init = useMemo(() => {
    const textMode = mode === "text";
    return {
      height,
      menubar: false,
      branding: false,
      statusbar: true,
      promotion: false,
      placeholder,
      forced_root_block: textMode ? "" : "p",
      toolbar: textMode
        ? "undo redo | mergeTags"
        : "undo redo | styles | bold italic underline | bullist numlist | alignleft aligncenter alignright | link table | code | mergeTags",
      plugins: textMode ? "" : "link lists code table autolink",
      valid_elements: textMode ? "#text,br" : undefined,
      setup: (editor: any) => {
        editor.ui.registry.addMenuButton("mergeTags", {
          text: "Merge Tags",
          fetch: (callback: any) => {
            const items = (mergeTags || []).map((mergeTag) => ({
              type: "menuitem",
              text: mergeTag.label,
              onAction: () => editor.insertContent(mergeTag.tag),
            }));
            callback(items);
          },
        });
      },
    };
  }, [mode, mergeTags, height, placeholder]);

  return (
    <Editor
      licenseKey="gpl"
      tinymceScriptSrc="/tinymce/tinymce.min.js"
      value={toEditorValue(content, mode)}
      init={init}
      onEditorChange={(newContent, editor) => {
        if (mode === "text") {
          setContent(editor.getContent({ format: "text" }));
          return;
        }

        setContent(newContent);
      }}
    />
  );
}
